import { useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function fingerprint(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Debounced auto-persist for config drafts. Skips the initial remote hydrate and
 * skips writes when the draft matches the last successful save.
 */
export function useAutoSave<T>(
  draft: T,
  save: (value: T) => Promise<void>,
  opts: {
    enabled?: boolean;
    debounceMs?: number;
    /** When false, skip this save attempt (e.g. invalid draft). */
    shouldSave?: (value: T) => boolean;
    onSkip?: (value: T) => void;
    onError?: (err: unknown) => void;
  } = {},
) {
  const {
    enabled = true,
    debounceMs = 500,
    shouldSave,
    onSkip,
    onError,
  } = opts;
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const readyRef = useRef(false);
  const lastSavedRef = useRef('');
  const saveRef = useRef(save);
  const shouldSaveRef = useRef(shouldSave);
  const onSkipRef = useRef(onSkip);
  const onErrorRef = useRef(onError);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflightRef = useRef(0);

  useEffect(() => {
    saveRef.current = save;
    shouldSaveRef.current = shouldSave;
    onSkipRef.current = onSkip;
    onErrorRef.current = onError;
  });

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const key = fingerprint(draft);
    if (!readyRef.current) {
      readyRef.current = true;
      lastSavedRef.current = key;
      return undefined;
    }
    if (key === lastSavedRef.current) return undefined;

    if (shouldSaveRef.current && !shouldSaveRef.current(draft)) {
      onSkipRef.current?.(draft);
      return undefined;
    }

    const timer = setTimeout(() => {
      const generation = ++inflightRef.current;
      setStatus('saving');
      void saveRef.current(draft)
        .then(() => {
          if (generation !== inflightRef.current) return;
          lastSavedRef.current = fingerprint(draft);
          setStatus('saved');
          if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
          resetTimerRef.current = setTimeout(() => setStatus('idle'), 1600);
        })
        .catch((err) => {
          if (generation !== inflightRef.current) return;
          setStatus('error');
          onErrorRef.current?.(err);
          if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
          resetTimerRef.current = setTimeout(() => setStatus('idle'), 2800);
        });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [draft, enabled, debounceMs]);

  return status;
}
